import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { compose } from 'utils/compose';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { Dialog, DialogContent, Paper, Container, Grid, CircularProgress } from '@mui/material';

import DialogHeader from './DialogHeader';
import { Hr } from 'components/DeliveryManager';
import { DialogActionButton, DialogFooter } from 'components/Dialog';

import useSearchParams from 'hooks/useSearchParams';
import api from 'utils/api';

const PREFIX = 'ConfirmSuggestedAddressDialog';

const classes = {
  root: `${PREFIX}-root`,
  text: `${PREFIX}-text`,
  diff: `${PREFIX}-diff`,
  description: `${PREFIX}-description`,
  label: `${PREFIX}-label`,
  value: `${PREFIX}-value`,
  input: `${PREFIX}-input`,
  btn: `${PREFIX}-btn`,
  form: `${PREFIX}-form`,
  paperContainer: `${PREFIX}-paperContainer`,
  paperContainerSelected: `${PREFIX}-paperContainerSelected`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`&.${classes.root}`]: {},

  [`& .${classes.text}`]: {
    color: theme.colorTokens.onSurfaceTextTitle,
    fontSize: '14px',
    marginTop: 0,
  },

  [`& .${classes.diff}`]: {
    color: 'red',
    fontSize: '14px',
    fontStyle: 'italic',
  },

  [`& .${classes.description}`]: {
    color: theme.colorTokens.onSurfaceTextHeadline,
    fontSize: '14px',
    fontWeight: 600,
  },

  [`& .${classes.label}`]: {
    color: theme.colorTokens.onSurfaceTextTitle,
    fontSize: '14px',
  },

  [`& .${classes.value}`]: {
    color: theme.colorTokens.onSurfaceTextDisplay,
    fontSize: '14px',
    fontWeight: 500,
  },

  [`& .${classes.input}`]: {
    fontSize: '12px',
  },

  [`& .${classes.btn}`]: {
    borderRadius: '2rem',
    marginLeft: '0 !important',
    whiteSpace: 'nowrap',
    minWidth: 'auto',
  },

  [`& .${classes.form}`]: {
    display: 'grid',
    gridAutoFlow: 'row',
    [theme.breakpoints.up('sm')]: {
      gridAutoFlow: 'column',
      gridTemplateColumns: '100px 1fr',
    },
  },

  [`& .${classes.paperContainer}`]: {
    padding: '12px 24px',
    '&:hover': {
      border: `1px solid ${theme.palette.secondary.main}`,
    },
  },

  [`& .${classes.paperContainerSelected}`]: {
    padding: '12px 24px',
    border: `2px solid ${theme.palette.secondary.main}`,
  },
}));

const SUGGESTED = 'SUGGESTED';
const ORIGINAL = 'ORIGINAL';

function ConfirmSuggestedAddressDialog({ store }) {
  const { trackingCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { deliveryStore } = store;
  const { delivery, loadingDelivery } = deliveryStore;
  const [loading, setLoading] = useState(false);
  const address = delivery?.shipment?.dropoff_address;
  const address2 = address?.street2 || '';
  const shipmentID = delivery?.shipment?.id;
  const addressComponents = delivery?.enriched_address?.enrichedAddress?.addressComponents;
  const [selected, setSelected] = useState(SUGGESTED);

  // Check if suggested address has required fields
  const suggestedAddress1 = !!addressComponents?.componentNotes?.street1 ? addressComponents?.streetLine : address?.street;
  const suggestedCity = !!addressComponents?.componentNotes?.city ? addressComponents?.city : address?.city;
  const suggestedState = !!addressComponents?.componentNotes?.state ? addressComponents?.state : address?.state;
  const suggestedZipcode = addressComponents?.zip;

  const isValidSuggested = !!suggestedAddress1 && !!suggestedCity && !!suggestedState && !!suggestedZipcode;

  const isSubmitDisabled = selected === SUGGESTED && !isValidSuggested;

  const handleSubmit = () => {
    setLoading(true);
    let url = '';
    let data = {};
    if (selected === SUGGESTED) {
      url = `delivery/${trackingCode}/${shipmentID}/confirm-suggested-address`;

      const zip5 = `${addressComponents?.zip}`;
      const zip4 = address.zipcode.includes('-') && !!addressComponents?.zip4 ? `-${addressComponents?.zip4}` : '';
      data = {
        street: !!addressComponents?.componentNotes?.street1 ? `${addressComponents?.streetLine}` : `${(address?.street, address?.street)}`,
        street2:
          !!addressComponents?.componentNotes?.street2 || !!addressComponents?.componentNotes?.secondaryAddressUnitDesignator
            ? `${addressComponents?.secondaryAddressLine}`
            : `${address2}`,
        city: addressComponents?.city,
        state: addressComponents?.state,
        zipcode: `${zip5}${zip4}`,
      };
    } else {
      url = `delivery/${trackingCode}/${shipmentID}/keep-original-address`;
    }

    api
      .post(url, data)
      .then(async (response) => {
        if (response.status !== 200)
          return toast.error(<span>{response?.data?.message || 'Something went wrong. Please try again!'}</span>, {
            hideProgressBar: true,
            theme: 'colored',
          });
        toast.success(<span>Address updated successfully!</span>, { hideProgressBar: true, theme: 'colored' });
        await deliveryStore.getDelivery(trackingCode);
        handleCancel();
      })
      .finally(() => setLoading(false));
  };

  const handleCancel = () => {
    searchParams.set('dialog', 'closed');
    setSearchParams(searchParams);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" className={classes.container}>
        <Grid style={{ height: 300 }} container alignItems="center" justifyContent="center">
          <CircularProgress color="primary" />
        </Grid>
      </Container>
    );
  }
  const showDiff = (str, orig) => {
    if (str === null || str === undefined) {
      //we will let empty strings through
      return <span className={classes.value}>{orig}</span>;
    }
    const isDiff = (str && !orig) || (orig && !str) || (str && orig && str.trim().toUpperCase() !== orig.trim().toUpperCase());
    if (!isDiff) {
      return <span className={classes.value}>{orig}</span>;
    } else {
      return <span className={classes.diff}>{!str ? 'N/A' : str}</span>;
    }
  };
  const showDiffZip = (addressComponents, orig) => {
    if (!addressComponents?.componentNotes?.zip && !addressComponents?.componentNotes?.zip4) {
      return <span className={classes.value}>{orig}</span>;
    }

    let zip5Class = classes.value;
    if (!!addressComponents?.componentNotes?.zip) {
      zip5Class = classes.diff;
    }

    let zip4 = '';
    if (orig.includes('-') && !!addressComponents?.zip4) {
      let zip4Class = classes.value;
      if (!!addressComponents?.componentNotes?.zip4) {
        zip4Class = classes.diff;
      }
      zip4 = <span className={zip4Class}>-{addressComponents?.zip4}</span>;
    }
    return (
      <>
        <span className={zip5Class}>{addressComponents.zip}</span>
        {zip4}
      </>
    );
  };

  if (loading || loadingDelivery) {
    return (
      <StyledDialog open fullWidth maxWidth="md" className={classes.root}>
        <Grid style={{ height: 300 }} container alignItems="center" justifyContent="center">
          <CircularProgress color="primary" />
        </Grid>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open maxWidth="md" fullWidth className={classes.root} data-testid="confirm-suggested-address-dialog">
      <DialogHeader title="Confirm Your Address" />
      <DialogContent>
        <p className={classes.text}>Please help us confirm if you want to use our suggested address</p>
        <Hr />
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper
              square
              variant="outlined"
              onClick={() => setSelected(SUGGESTED)}
              className={selected === SUGGESTED ? classes.paperContainerSelected : classes.paperContainer}
            >
              <p className={classes.description}>Suggested Address:</p>
              <div className={classes.form}>
                <p className={classes.label}>Address 1:</p>
                <p className={classes.value} data-testid="suggested-address-1">
                  {showDiff(!!addressComponents?.componentNotes?.street1 ? addressComponents?.streetLine : address?.street, address?.street)}
                </p>
              </div>
              <div className={classes.form}>
                <p className={classes.label}>Address 2:</p>
                <p className={classes.value} data-testid="suggested-address-2">
                  {showDiff(
                    !!addressComponents?.componentNotes?.street2 || !!addressComponents?.componentNotes?.secondaryAddressUnitDesignator
                      ? addressComponents?.secondaryAddressLine
                      : address2,
                    address2,
                  )}
                </p>
              </div>
              <div className={classes.form}>
                <p className={classes.label}>City:</p>
                <p className={classes.value} data-testid="suggested-city">
                  {showDiff(!!addressComponents?.componentNotes?.city ? addressComponents?.city : address?.city, address?.city)}
                </p>
              </div>
              <div className={classes.form}>
                <p className={classes.label}>State:</p>
                <p className={classes.value} data-testid="suggested-state">
                  {showDiff(!!addressComponents?.componentNotes?.state ? addressComponents?.state : address?.state, address?.state)}
                </p>
              </div>
              <div className={classes.form}>
                <p className={classes.label}>Zipcode:</p>
                <p className={classes.value} data-testid="suggested-zipcode">{showDiffZip(addressComponents, address?.zipcode)}</p>
              </div>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper
              square
              variant="outlined"
              onClick={() => setSelected(ORIGINAL)}
              className={selected === ORIGINAL ? classes.paperContainerSelected : classes.paperContainer}
            >
              <p className={classes.description}>Original Address:</p>
              <div className={classes.form}>
                <p className={classes.label}>Address 1:</p>
                <p className={classes.value} data-testid="original-address-1">{address?.street || 'N/A'}</p>
              </div>
              <div className={classes.form}>
                <p className={classes.label}>Address 2:</p>
                <p className={classes.value} data-testid="original-address-2">{address2 || 'N/A'}</p>
              </div>
              <div className={classes.form}>
                <p className={classes.label}>City:</p>
                <p className={classes.value} data-testid="original-city">{address?.city}</p>
              </div>
              <div className={classes.form}>
                <p className={classes.label}>State:</p>
                <p className={classes.value} data-testid="original-state">{address?.state}</p>
              </div>
              <div className={classes.form}>
                <p className={classes.label}>Zipcode:</p>
                <p className={classes.value} data-testid="original-zipcode">{address?.zipcode}</p>
              </div>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogFooter>
        <DialogActionButton size="medium" variant="outlined" color="primary" onClick={handleCancel} disableElevation>
          Cancel
        </DialogActionButton>
        <DialogActionButton size="medium" variant="contained" color="primary" onClick={handleSubmit} disableElevation disabled={isSubmitDisabled}>
          {SUGGESTED === selected ? 'Use Suggested' : 'Keep Original'}
        </DialogActionButton>
      </DialogFooter>
    </StyledDialog>
  );
}

export default compose(inject('store'), observer)(ConfirmSuggestedAddressDialog);
